import { useUnit } from 'effector-react';
import { type FC } from 'react';
import { Pagination, ScrollArea, Stack } from '@mantine/core';
import { usePayoutTradersList, type PayoutTrader, type PayoutTraderFilters } from '~/entities/payout';
import { type PaginationModel } from '~/shared/lib/pagination';
import { Table } from '~/shared/ui/Table';
import { createTableData } from '../utils';

interface TradersListProps {
    filter: PayoutTraderFilters;
    polling?: boolean;
    onClick?: (trader: PayoutTrader) => void;
    paginationModel: PaginationModel;
}

export const TradersList: FC<TradersListProps> = ({ filter, onClick, paginationModel, polling = false }) => {
    const pagination = useUnit(paginationModel.$pagination);

    const traders = usePayoutTradersList(
        {
            'filter.statuses': filter.statuses,
            'filter.workingStatuses': filter.workingStatuses,
            'pagination.page': pagination.page.toString(),
            'pagination.perPage': pagination.perPage.toString(),
            'filter.traderName.value': filter.traderName?.value,
            'filter.bankAccountId': filter.bankAccountId,
            'filter.workStrategies': filter.workStrategies,
        },
        polling ? 5000 : false,
    );

    const total = Number(traders.data?.pagination?.total);
    const totalPages = Math.ceil(total / pagination.perPage);

    const tableData = createTableData({
        traders: traders.data?.items,
    });

    const handleRowClick = (item: PayoutTrader) => {
        return onClick?.(item);
    };

    const handleChangeCurrentPage = (page: number) => {
        paginationModel.setPage(page - 1);
    };

    return (
        <Stack>
            <ScrollArea h={512}>
                <Table
                    striped
                    highlightOnHover
                    data={tableData}
                    onRowClick={handleRowClick}
                    loading={traders.isLoading}
                />
            </ScrollArea>

            {totalPages > 1 && (
                <Pagination
                    total={totalPages}
                    value={pagination.page + 1}
                    onChange={handleChangeCurrentPage}
                    size='sm'
                />
            )}
        </Stack>
    );
};
